/* eslint-disable react-doctor/no-inline-exhaustive-style, react-doctor/no-tiny-text */
"use client"

import { Download } from 'lucide-react'
import { CONDENSED_INSTALL_STEPS } from '@/lib/install-steps'

// Usage example:
// <InstallationWizard onDownloadClick={() => handleDownload()} />

interface InstallationWizardProps {
  soundTitle?: string
  soundFormat?: string
  soundSize?: string
  onDownloadClick?: () => void
  className?: string
}

// One leading download row, then the canonical condensed steps verbatim from
// lib/install-steps.ts (ANTI_SLOP rule 4: all install surfaces agree).
const STEPS = [
  {
    title: 'Download LockChime.wav',
    detail: 'Already named and Tesla-ready.',
    hasDownloadCta: true,
  },
  ...CONDENSED_INSTALL_STEPS.map((step) => ({
    title: step.title,
    detail: step.body,
    hasDownloadCta: false,
  })),
]

export function InstallationWizard({
  // soundTitle, soundFormat, soundSize kept for backwards compat but unused in this layout
  onDownloadClick,
  className = '',
}: InstallationWizardProps) {
  return (
    <div
      className={className}
      style={{ borderTop: '1px solid var(--tls-border-subtle)' }}
    >
      {STEPS.map((step, index) => {
        const isLast = index === STEPS.length - 1

        return (
          <div
            key={step.title}
            style={{
              display: 'grid',
              gridTemplateColumns: onDownloadClick && step.hasDownloadCta
                ? 'auto minmax(0, 1fr) auto'
                : 'auto minmax(0, 1fr)',
              alignItems: 'center',
              gap: 14,
              padding: '14px 0',
              borderBottom: isLast ? 'none' : '1px solid var(--tls-border-subtle)',
              background: 'transparent',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'var(--tls-font-body)',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--tls-fg-subtle)',
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </span>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--tls-fg-strong)',
                  lineHeight: 1.3,
                }}
              >
                {step.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--tls-fg-muted)',
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                {step.detail}
              </div>
            </div>

            {/* Download CTA inline on step 1. Outlined, not filled: the
                player's "Download LockChime.wav" is the one accent-filled
                primary on this page, and §02.1 allows one primary per panel:
                this is the outlined half of the filled/outlined pair
                (owner ruling 2026-08-15). */}
            {step.hasDownloadCta && onDownloadClick && (
              // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
              <button type="button"
                onClick={onDownloadClick}
                data-cuelume-press="press"
                data-cuelume-release="pulse"
                aria-label="Download LockChime.wav"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  minHeight: 'var(--touch-target-min)',
                  borderRadius: 'var(--radius-pill)',
                  background: 'transparent',
                  color: 'var(--tls-fg-strong)',
                  border: '1px solid var(--tls-fg-strong)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'background var(--motion-duration-fast) var(--motion-ease-standard), color var(--motion-duration-fast) var(--motion-ease-standard)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--tls-fg-strong)'
                  e.currentTarget.style.color = 'var(--tls-canvas)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = 'var(--tls-fg-strong)'
                }}
              >
                <Download aria-hidden="true" style={{ width: 13, height: 13 }} />
                Download
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default InstallationWizard
